import React, { useState, useEffect, useContext } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Box,
  Tooltip,
  IconButton,
  Collapse,
  useMediaQuery
} from '@material-ui/core';
import {
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@material-ui/icons';
import { HoursContext } from '../contexts/HoursContext';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  compactCard: {
    '& .MuiCardContent-root': {
      padding: `${theme.spacing(1)}px !important`,
      [theme.breakpoints.up('sm')]: {
        padding: `${theme.spacing(1.5)}px !important`,
      },
      '&:last-child': {
        paddingBottom: `${theme.spacing(1)}px !important`,
        [theme.breakpoints.up('sm')]: {
          paddingBottom: `${theme.spacing(1.5)}px !important`,
        },
      },
    },
  },
  quotaBar: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  quotaText: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
  statusChip: {
    marginLeft: theme.spacing(1),
    height: 24, // Smaller chip
  },
  details: {
    marginTop: theme.spacing(1),
  },
  expandButton: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
}));

const TogglQuotaStatus = () => {
  const classes = useStyles();
  const { togglQuotaStatus, checkTogglQuota } = useContext(HoursContext);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Check quota status on component mount
    checkTogglQuota();
    
    // Refresh every 5 minutes
    const interval = setInterval(checkTogglQuota, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (!togglQuotaStatus || !togglQuotaStatus.quota) {
    return null;
  }

  const { quota } = togglQuotaStatus;
  const usagePercentage = (quota.used / quota.limit) * 100;
  
  // Determine status and color
  let status = 'good';
  let statusColor = 'primary';
  let statusIcon = <CheckCircleIcon />;
  let statusText = 'Good';
  
  if (quota.remaining === 0) {
    status = 'exceeded';
    statusColor = 'error';
    statusIcon = <ErrorIcon />;
    statusText = 'Limit Reached';
  } else if (quota.remaining <= 5) {
    status = 'warning';
    statusColor = 'warning';
    statusIcon = <WarningIcon />;
    statusText = 'Low Quota';
  }

  const formatTime = (seconds) => {
    if (seconds <= 0) return 'Now';
    const minutes = Math.ceil(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  return (
    <Card className={`${classes.root} ${classes.compactCard}`} variant="outlined">
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="body2" color="textSecondary">
            Toggl API Quota
          </Typography>
          <Box display="flex" alignItems="center">
            <Chip
              icon={statusIcon}
              label={statusText}
              color={statusColor}
              size="small"
              className={classes.statusChip}
            />
            <IconButton
              className={`${classes.expandButton} ${expanded ? classes.expandOpen : ''}`}
              onClick={() => setExpanded(!expanded)}
              aria-expanded={expanded}
              size="small"
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        </Box>

        <Box className={classes.quotaText}>
          <Typography variant="body2">
            {quota.used} / {quota.limit} requests per hour {quota.source && `(${quota.source === 'toggl_api_real' ? 'Real-time' : 'Estimated'})`}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {quota.remaining} remaining
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={usagePercentage}
          color={statusColor}
          className={classes.quotaBar}
          style={{ height: 6 }}
        />

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box className={classes.details}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Quota resets in: {formatTime(quota.resetInSeconds)}
            </Typography>
            
            {status === 'exceeded' && (
              <Typography variant="body2" color="error">
                ⚠️ You've reached your hourly limit. Please wait {formatTime(quota.resetInSeconds)} 
                before importing more Toggl data, or upgrade to a paid Toggl plan.
              </Typography>
            )}
            
            {status === 'warning' && (
              <Typography variant="body2" color="textSecondary">
                ⚡ Low quota remaining. Consider waiting {formatTime(quota.resetInSeconds)} 
                for a fresh quota, or use imports sparingly.
              </Typography>
            )}
            
            {status === 'good' && (
              <Typography variant="body2" color="textSecondary">
                ✅ Plenty of quota available for Toggl imports.
              </Typography>
            )}

            <Box mt={1}>
              <Typography variant="caption" color="textSecondary" display="block">
                {quota.source === 'toggl_api' ? '📡 Real-time data from Toggl API' : '📊 Estimated based on local tracking'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Free Toggl accounts: 30 requests/hour • 
                <Tooltip title="Organizations, workspaces, projects, clients, and time entries each count as separate requests">
                  <InfoIcon style={{ fontSize: 14, marginLeft: 4, verticalAlign: 'middle' }} />
                </Tooltip>
              </Typography>
              {quota.warning && (
                <Typography variant="caption" color="error" display="block">
                  ⚠️ {quota.warning}
                </Typography>
              )}
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default TogglQuotaStatus;